import { useState } from 'react'

import {
  SmallSecondaryBadge,
  Spinner,
  Text,
  Tooltip,
} from '@joor/design-system'
import styled from 'styled-components'

import { UnmatchedUpload } from '__generated__/atlas-types'

import { encodeURIComponentImageName, getSizedImageUrl } from 'utils/images'

import {
  GALLERY_IMAGE_DIMENSION,
  PRODUCT_IMAGES_LIMIT,
} from 'features/Linesheets/Modals/ManageImagesModal/manageImagesModal.constants'
import { GalleryIds } from 'features/Linesheets/Modals/ManageImagesModal/manageImagesModal.ids'
import messages from 'features/Linesheets/Modals/ManageImagesModal/manageImagesModal.messages'
import useStore from 'features/Linesheets/Modals/ManageImagesModal/store/useManageImagesStore'

const Grid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  row-gap: 10px;
  justify-items: center;
  overflow-y: auto;
`

const GalleryItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  @media (min-width: 768px) {
    width: 110px;
  }

  @media (min-width: 1024px) {
    width: 125px;
  }

  @media (min-width: 1280px) {
    width: 165px;
  }
`

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  border-radius: 4px;
  box-sizing: border-box;
  overflow: hidden;
  transition: all 0.05s ease-in-out;

  @media (min-width: 768px) {
    height: 152px;
  }

  @media (min-width: 1024px) {
    height: 172px;
  }

  @media (min-width: 1280px) {
    height: 226px;
  }

  > div {
    position: absolute;
    top: 8px;
    right: 8px;
  }

  &.selected {
    border: 4px solid var(--color-secondary-800);
  }

  &.disabled::before {
    position: absolute;
    content: '';
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    border-radius: 4px;
    box-sizing: border-box;
    background-color: var(--color-overlay-light);
    border: 4px solid var(--color-primary-200);
    z-index: 1;
  }
`

const Image = styled.img`
  width: 100%;
  height: auto;
  object-fit: cover;
`

const Loader = styled(Spinner)`
  position: absolute;
  top: 47%;
  left: 47%;

  & circle {
    stroke: var(--color-secondary-600);
  }
`

const ImageName = styled(Text)`
  overflow: hidden;
  text-overflow: ellipsis;

  &&& {
    white-space: nowrap;
  }
`

type GalleryGridProps = {
  images: UnmatchedUpload[]
}

const GalleryGrid = ({ images }: GalleryGridProps): JSX.Element => {
  const productImages = useStore((state) => state.productImages)
  const selectedImages = useStore((state) => state.selectedImages)
  const setSelectedImages = useStore((state) => state.setSelectedImages)
  const styleColors = useStore((state) => state.styleColors)
  const [isLoading, setIsLoading] = useState(true)

  const canSelectImages =
    productImages.length + selectedImages.size < PRODUCT_IMAGES_LIMIT

  const handleImageClick = (image: UnmatchedUpload) => {
    const newSelectedImages = new Map(selectedImages)
    const key = image?.unmatchedUploadId as string

    if (selectedImages.has(key)) {
      newSelectedImages.delete(key)
    } else if (canSelectImages) {
      newSelectedImages.set(key, image)
    }

    setSelectedImages({ selectedImages: newSelectedImages })
  }

  return (
    <Grid>
      {images.map((image) => {
        const isAssigned =
          productImages.some(
            (img) =>
              img.unmatchedUploadId &&
              image.unmatchedUploadId &&
              img.unmatchedUploadId === image.unmatchedUploadId,
          ) ||
          styleColors.some(
            (color) =>
              color.unmatchedUploadId &&
              image.unmatchedUploadId &&
              color.unmatchedUploadId === image.unmatchedUploadId,
          )
        const selectionIndex = Array.from(selectedImages.keys()).indexOf(
          image.unmatchedUploadId as string,
        )
        const isSelected = selectionIndex >= 0

        return (
          <GalleryItem
            key={image.unmatchedUploadId}
            data-testid={`${GalleryIds.GalleryItem}__${image.unmatchedUploadId}`}
          >
            <Tooltip
              content={
                isSelected || canSelectImages ? '' : messages.selectLimitReached
              }
              variant="warning"
            >
              <ImageContainer
                data-testid={`${GalleryIds.ImageContainer}__${image.unmatchedUploadId}`}
                className={`${isSelected ? 'selected' : ''} ${
                  isAssigned ? 'disabled' : ''
                }`}
                onClick={
                  isAssigned
                    ? undefined
                    : (e: React.MouseEvent<HTMLDivElement>) => {
                        e.preventDefault()
                        handleImageClick(image)
                      }
                }
              >
                <Image
                  data-testid={`${GalleryIds.Image}__${image.unmatchedUploadId}`}
                  alt={image.fileName as string}
                  src={getSizedImageUrl(
                    encodeURIComponentImageName(image.url),
                    GALLERY_IMAGE_DIMENSION,
                  )}
                  onLoad={() => setIsLoading(false)}
                />
                {isLoading && <Loader />}
                {isSelected && (
                  <SmallSecondaryBadge value={selectionIndex + 1} />
                )}
              </ImageContainer>
            </Tooltip>
            <ImageName
              data-testid={`${GalleryIds.ImageName}__${image.unmatchedUploadId}`}
              fontWeight="medium"
              fontSize="extrasmall"
              letterSpacing="tight"
              lineHeight="12"
              title={image.fileName as string}
            >
              {image.fileName}
            </ImageName>
          </GalleryItem>
        )
      })}
    </Grid>
  )
}

export default GalleryGrid
